import { createSlice } from '@reduxjs/toolkit';

import {
  incomingMessageReceived,
  type IncomingMessagePayload,
} from '@/store/actions/poc/incomingMessageReceived';
import { disconnectP2PStackThunk } from '@/store/thunks/poc/connection/disconnectP2PStackThunk';
import { initializeP2PStackThunk } from '@/store/thunks/poc/connection/initializeP2PStackThunk';
import { publishMessageThunk } from '@/store/thunks/poc/messaging/publishMessageThunk';
import { extractThunkError } from '@/store/utils';

export interface RecentMessagingEvent {
  messageType: string;
  fromPeer: string;
  topic: string;
  timestamp: number;
  messageContent?: string;
}

export interface SingleMessagingState {
  sixId: string;
  status: 'idle' | 'initializing' | 'ready' | 'error';
  error: string | null;
  recentMessages: RecentMessagingEvent[];
  maxRecentMessages: number;
  stats: {
    totalMessagesReceived: number;
    totalMessagesSent: number;
    lastReceivedAt: number | null;
    lastSentAt: number | null;
  };
}

export interface MessagingState {
  sixMessaging: SingleMessagingState[];
}

const MAX_RECENT_MESSAGES = 50;

const initialState: MessagingState = {
  sixMessaging: [],
};

const createInitialSixMessaging = (sixId: string): SingleMessagingState => ({
  sixId,
  status: 'idle',
  error: null,
  recentMessages: [],
  maxRecentMessages: MAX_RECENT_MESSAGES,
  stats: {
    totalMessagesReceived: 0,
    totalMessagesSent: 0,
    lastReceivedAt: null,
    lastSentAt: null,
  },
});

const ensureSixMessaging = (
  sixMessagingArray: SingleMessagingState[],
  sixId: string
): SingleMessagingState[] =>
  sixMessagingArray.some((entry) => entry.sixId === sixId)
    ? sixMessagingArray
    : [...sixMessagingArray, createInitialSixMessaging(sixId)];

const updateSixMessaging = (
  sixMessagingArray: SingleMessagingState[],
  sixId: string,
  updater: (entry: SingleMessagingState) => SingleMessagingState
): SingleMessagingState[] =>
  sixMessagingArray.map((entry) => (entry.sixId === sixId ? updater(entry) : entry));

const recordIncomingMessage = (
  sixMessaging: SingleMessagingState,
  payload: IncomingMessagePayload
): SingleMessagingState => {
  const nextMessage: RecentMessagingEvent = {
    messageType: payload.result.type,
    fromPeer: payload.stats.fromPeer,
    topic: payload.stats.topic,
    timestamp: payload.stats.timestamp,
    messageContent: payload.stats.messageContent,
  };

  const recentMessages = [...sixMessaging.recentMessages, nextMessage].slice(
    -sixMessaging.maxRecentMessages
  );

  return {
    ...sixMessaging,
    status: 'ready',
    error: null,
    recentMessages,
    stats: {
      ...sixMessaging.stats,
      totalMessagesReceived: sixMessaging.stats.totalMessagesReceived + 1,
      lastReceivedAt: payload.stats.timestamp,
    },
  };
};

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeP2PStackThunk.pending, (state, action) => {
        if (action.meta.arg.autoInitializeMessaging === false) {
          return;
        }

        state.sixMessaging = ensureSixMessaging(
          state.sixMessaging,
          action.meta.arg.sixId
        );
        state.sixMessaging = updateSixMessaging(
          state.sixMessaging,
          action.meta.arg.sixId,
          (entry) => ({
            ...entry,
            status: 'initializing',
            error: null,
          })
        );
      })
      .addCase(initializeP2PStackThunk.fulfilled, (state, action) => {
        if (!action.payload.messagingInitialized) {
          return;
        }

        state.sixMessaging = ensureSixMessaging(
          state.sixMessaging,
          action.payload.sixId
        );
        state.sixMessaging = updateSixMessaging(
          state.sixMessaging,
          action.payload.sixId,
          (entry) => ({
            ...entry,
            status: 'ready',
            error: null,
          })
        );
      })
      .addCase(initializeP2PStackThunk.rejected, (state, action) => {
        if (action.meta.arg.autoInitializeMessaging === false) {
          return;
        }

        const { message } = extractThunkError(action.payload);
        state.sixMessaging = ensureSixMessaging(
          state.sixMessaging,
          action.meta.arg.sixId
        );
        state.sixMessaging = updateSixMessaging(
          state.sixMessaging,
          action.meta.arg.sixId,
          (entry) => ({
            ...entry,
            status: 'error',
            error: message ?? action.error.message ?? 'Unknown error',
          })
        );
      })
      .addCase(incomingMessageReceived, (state, action) => {
        state.sixMessaging = ensureSixMessaging(
          state.sixMessaging,
          action.payload.sixId
        );
        state.sixMessaging = updateSixMessaging(
          state.sixMessaging,
          action.payload.sixId,
          (entry) => recordIncomingMessage(entry, action.payload)
        );
      })
      .addCase(publishMessageThunk.fulfilled, (state, action) => {
        state.sixMessaging = ensureSixMessaging(
          state.sixMessaging,
          action.payload.sixId
        );
        state.sixMessaging = updateSixMessaging(
          state.sixMessaging,
          action.payload.sixId,
          (entry) => ({
            ...entry,
            status: 'ready',
            error: null,
            stats: {
              ...entry.stats,
              totalMessagesSent: entry.stats.totalMessagesSent + 1,
              lastSentAt: action.payload.timestamp,
            },
          })
        );
      })
      .addCase(disconnectP2PStackThunk.fulfilled, (state, action) => {
        state.sixMessaging = state.sixMessaging.filter(
          (entry) => entry.sixId !== action.payload.sixId
        );
      });
  },
});

export default messagingSlice.reducer;
